// shared-lib/cabinet-runs/sizing.js
//
// ONE INTENT: is this run built to STANDARD sizes, or to MATCH WHAT IS THERE?
//
// The user's framing, and the right one (2026-09-26): a vanity recovered from
// a scan can either be rebuilt out of the sizes a supplier sells, or kept at
// the dimensions the room actually contains. Several answers follow from that
// single choice, and while each was decided separately they could disagree:
//
//   | | as-built | standard |
//   | body depth | what the scan measured | the profile's own (or Narrow) |
//   | counter overhang | the strip the room granted, often none | the profile's, grown to fit a bowl |
//   | sink base width | the run — a scanned vanity is ONE box | the narrowest standard base that clears the bowl |
//
// STAMPED BY WHOEVER DECIDES, and only by them: the derive stamps the answer
// its SOURCE implies (a scan is matching what is there; a vanity the builder
// BUILT is standard sizes by definition), and a depth pick re-stamps it,
// because picking a depth IS the user answering this question. One field, and
// it holds the ANSWER — not the three sizes that follow from it, which is what
// drifted before: depth said standard while the counter overhang still
// described the carcass the scan found, so one "Standard 21"" pick produced
// three different vanities.
//
// Inferring it from the depth alone — this module's first shape — cannot work,
// and a BUILT vanity is why. The builder builds at the profile's standard
// depth, so its live depth equals its measured depth and the inference calls
// it as-built: measured over widths 24"-40.3", every generated vanity came out
// as ONE full-width box, while the panel showed "Standard" selected with no
// "As built" chip to escape through (an on-list measurement offers none) and
// picking Standard was a no-op, because the depth was already standard. The
// user's ruling is that a full-width sink base belongs to CUSTOM cabinets and
// never to standard, and there was no way to honour it while the answer was
// only ever guessed at from a number that agreed with both.
//
// An unstamped run — everything derived before this field — falls back to that
// inference, so nothing already stored changes meaning.
//
// A run with nothing measured — a DRAWN run — is standard by definition:
// there is nothing to match.
//
// Pure. No I/O.

const AS_BUILT = 'as-built';
const STANDARD = 'standard';

// A sixteenth of a millimetre: two lengths the user would call the same. The
// same tolerance `applyRunDepth` compares depths with, deliberately — the two
// have to agree about when a pick has moved the run off its measurement.
const SAME = 1e-4;

/**
 * Which way this run is being sized — the stamped answer where there is one.
 *
 * @param {Object} run a stored or hydrated run.
 * @returns {'as-built'|'standard'}
 */
function sizingOf(run) {
  const stamped = run?.sizing;
  if (stamped === AS_BUILT || stamped === STANDARD) return stamped;
  // Unstamped: infer, for the runs derived before the field existed. Its
  // blind spot is above — a vanity built AT a standard depth reads as though
  // it were matching one — which is exactly why the answer is stamped now.
  return standsAtMeasuredDepth(run) ? AS_BUILT : STANDARD;
}

/** Is this run being kept at the dimensions the scan found? */
function isAsBuilt(run) {
  return sizingOf(run) === AS_BUILT;
}

/**
 * A DIFFERENT question, and the one the COUNTER asks: does the overhang this
 * run recorded still describe the body it was measured against?
 *
 * Not the sizing intent, though the two coincide on a scanned vanity. The
 * granted front strip is an OBSERVATION whichever way the run is sized —
 * `buildVanityCountertop` runs a BUILT vanity's counter through
 * `computeCountertopOverhangs` too, so a toilet standing in the strip
 * suppresses it for the builder exactly as it does for the scan — and a
 * standard-sized run that re-derived the overhang from a rule would push that
 * counter into the space the room already refused. So the counter follows the
 * measurement while the measurement still applies, and only the SINK BASE
 * follows the intent.
 *
 * What makes it stop applying is a depth change, and `applyRunDepth` enforces
 * that from the other side: any pick that is not a return to the measured
 * depth deletes the recorded overhang.
 */
function standsAtMeasuredDepth(run) {
  const measured = run?.measured?.depth;
  if (!(measured > 0)) return false;
  const live = run?.depth;
  // Never picked: the derive wrote both together, so they still agree.
  if (!(live > 0)) return true;
  return Math.abs(live - measured) < SAME;
}

module.exports = {
  sizingOf,
  isAsBuilt,
  standsAtMeasuredDepth,
  AS_BUILT,
  STANDARD,
  SAME,
};
